import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import * as graphql from '@treams/treams-network';
import { AddIcon, Avatar, Button, FlexGap, Label, Sidebar, tokens } from '@treams/treams-ui';

const SUBJECT_AVATAR_SIZE = 24;

const Pane = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${tokens.gap.section}px;
	padding: ${tokens.padding.control}px;
	box-sizing: border-box;
	min-height: 100%;
	background: ${tokens.surface.secondary};
`;

type AgentChatSidebarProps = {
	chats: graphql.AgentChatsQuery['agentChats'];
	loading?: boolean;
};

export function AgentChatSidebar(props: AgentChatSidebarProps) {
	const { t } = useTranslation();

	const sortedChats = useMemo(
		() =>
			props.chats.toSorted(
				(a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
			),
		[props.chats],
	);

	return (
		<Pane>
			<Button
				type="secondary"
				icon={<AddIcon />}
				title={t('aiAgent.newChat')}
				link="/assistant/new"
				alignment="start"
			/>

			<FlexGap flexDirection="column" gap={tokens.gap.list} width="100%">
				<Label>{t('aiAgent.history')}</Label>

				<div>
					{props.loading && <Sidebar.Item.Skeleton small />}

					{!props.loading && props.chats.length === 0 && (
						<Sidebar.Custom small>{t('aiAgent.noChats')}</Sidebar.Custom>
					)}

					{sortedChats.map((chat) => (
						<Sidebar.Item
							key={chat.id}
							small
							icon={
								chat.subject ? <Avatar user={chat.subject} size={SUBJECT_AVATAR_SIZE} /> : undefined
							}
							title={chat.title}
							path={`/assistant/${chat.id}`}
							exact
							overflow="hidden"
						/>
					))}
				</div>
			</FlexGap>
		</Pane>
	);
}
